import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
} from "@mui/material";
// @ts-ignore
import { ResizableBox, ResizableCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";

// データ型
export type AdministratorListItem = {
  id: string | number;
  userNumber?: number;
  lastName?: string;
  firstName?: string;
  mailAddress?: string;
  baseId?: number;
  base?: string;
  baseIds?: number[];
  bases?: string[];
  roomId?: number;
  password?: string;
  lineName?: string;
  shopName?: string;
  introducer?: string;
  driverName?: string;
  category?: string;
  shippingNum?: number;
  largeProduct?: boolean;
};

type Props = {
  users: AdministratorListItem[];
  checkedIds: Set<string>;
  onCheckedChange: (id: string) => void;
  onCheckedChangeAll: (checkedIds: Set<string>) => void;
  onRowClick: (user: AdministratorListItem) => void;
  footerHeight: number;
};

const AdministratorListTable: React.FC<Props> = ({
  users,
  checkedIds,
  onCheckedChange,
  onCheckedChangeAll,
  onRowClick,
  footerHeight,
}) => {
  // フィルター条件
  const [filters, setFilters] = useState<Partial<AdministratorListItem>>({});
  const [columnWidths, setColumnWidths] = useState<number[]>([
    30, // 承認
    150, // ユーザー
    200, // メール
    120, // 拠点
    120, // roomId
  ]);

  // フィルター条件の更新
  const handleFilterChange = (
    key: keyof AdministratorListItem | "fullName",
    value: string
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // フィルタリング
  const filteredUsers = users.filter((user) => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      let targetValue = "";
      if (key === "fullName") {
        targetValue = (user.lastName ?? "") + (user.firstName ?? "");
      } else {
        const targetValue = user[key as keyof AdministratorListItem] ?? "";
        return String(targetValue).includes(String(value));
      }
      return String(targetValue).includes(String(value));
    });
  });

  // 列幅更新
  const handleResize = (index: number, newWidth: number) => {
    const updated = [...columnWidths];
    updated[index] = newWidth;
    setColumnWidths(updated);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "auto",
        height: `calc(100vh - 130px - ${
          checkedIds.size > 0 ? footerHeight * 1.3 : 0
        }px)`,
        overflowY: "auto",
        overflowX: "auto",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
        <TableHead>
          {/* ヘッダー行 */}
          <TableRow>
            <TableCell align="center" sx={{ width: 20, padding: 0 }}>
              <Checkbox
                checked={
                  checkedIds.size === filteredUsers.length &&
                  filteredUsers.length > 0
                }
                indeterminate={
                  checkedIds.size > 0 && checkedIds.size < filteredUsers.length
                }
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    const newSet = new Set(
                      filteredUsers.map((user) => String(user.id))
                    );
                    onCheckedChangeAll(newSet);
                  } else {
                    onCheckedChangeAll(new Set());
                  }
                }}
              />
            </TableCell>
            {["ユーザー", "メールアドレス", "拠点", "roomId"].map(
              (label, idx) => (
                <TableCell
                  key={label}
                  align="center"
                  style={{ width: columnWidths[idx + 1], whiteSpace: "nowrap" }}
                >
                  <ResizableBox
                    width={columnWidths[idx + 1]}
                    height={30}
                    axis="x"
                    resizeHandles={["e"]}
                    onResizeStop={(_: unknown, data: ResizableCallbackData) => {
                      handleResize(idx + 1, data.size.width);
                    }}
                  >
                    <div
                      style={{
                        width: columnWidths[idx + 1],
                        textAlign: "center",
                      }}
                    >
                      {label}
                    </div>
                  </ResizableBox>
                </TableCell>
              )
            )}
          </TableRow>

          {/* フィルター行 */}
          <TableRow>
            <TableCell />
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("fullName", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) =>
                  handleFilterChange("mailAddress", e.target.value)
                }
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("base", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("roomId", e.target.value)}
              />
            </TableCell>
          </TableRow>
        </TableHead>

        {/* データ行 */}
        <TableBody>
          {filteredUsers.map((user) => (
            <TableRow
              key={user.id}
              onClick={() => {
                onRowClick(user);
              }}
            >
              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checkedIds.has(String(user.id))}
                  onChange={(e) => {
                    e.stopPropagation();
                    onCheckedChange(String(user.id));
                  }}
                />
              </TableCell>
              <TableCell>
                {(user.lastName ?? "") + (user.firstName ?? "")}
              </TableCell>
              <TableCell>{user.mailAddress}</TableCell>
              <TableCell>{user.base}</TableCell>
              <TableCell>{user.roomId}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AdministratorListTable;
